import { api } from './api';
import type { AgentDecision, AgentState } from '@/stores/agentStore';

export interface AgentConfig {
  id: string;
  strategy: string;
  riskTolerance: string;
  maxPositionSizePct: number;
  stopLossPct: number;
  takeProfitPct: number;
  maxDailyTrades: number;
  maxDailyLoss: number;
  watchlist: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgentConfigUpdate {
  strategy?: string;
  risk_tolerance?: string;
  max_position_size_pct?: number;
  stop_loss_pct?: number;
  take_profit_pct?: number;
  max_daily_trades?: number;
  max_daily_loss?: number;
  watchlist?: string;
}

/**
 * Transform snake_case agent config from backend to camelCase for frontend
 */
function transformAgentConfig(res: Record<string, unknown>): AgentConfig {
  return {
    id: String(res.id ?? ''),
    strategy: String(res.strategy ?? 'momentum'),
    riskTolerance: String(res.risk_tolerance ?? 'medium'),
    maxPositionSizePct: Number(res.max_position_size_pct ?? 25),
    stopLossPct: Number(res.stop_loss_pct ?? 5),
    takeProfitPct: Number(res.take_profit_pct ?? 10),
    maxDailyTrades: Number(res.max_daily_trades ?? 20),
    maxDailyLoss: Number(res.max_daily_loss ?? 5000),
    watchlist: String(res.watchlist ?? 'AAPL,MSFT,GOOGL,AMZN,TSLA'),
    createdAt: String(res.created_at ?? ''),
    updatedAt: String(res.updated_at ?? ''),
  };
}

export interface AgentDecisionListResponse {
  decisions: AgentDecision[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Transform snake_case agent state from backend to camelCase for frontend
 */
function transformAgentState(res: Record<string, unknown>): AgentState {
  return {
    status: (res.status as AgentState['status']) ?? 'stopped',
    strategyName: String(res.strategy_name ?? 'default'),
    lastAction: res.last_action ? String(res.last_action) : null,
    lastActionAt: res.last_action_at ? String(res.last_action_at) : null,
    dailyPnl: Number(res.daily_pnl ?? 0),
    tradesToday: Number(res.trades_today ?? 0),
    errorMessage: res.error_message ? String(res.error_message) : null,
    startedAt: res.started_at ? String(res.started_at) : null,
  };
}

/**
 * Transform snake_case agent decision from backend to camelCase for frontend
 */
function transformDecision(d: Record<string, unknown>): AgentDecision {
  return {
    id: String(d.id ?? ''),
    decisionType: (d.decision_type as AgentDecision['decisionType']) ?? 'hold',
    symbol: String(d.symbol ?? ''),
    quantity: d.quantity != null ? Number(d.quantity) : null,
    price: d.price != null ? Number(d.price) : null,
    notional: d.notional != null ? Number(d.notional) : null,
    reasoning: String(d.reasoning ?? ''),
    confidence: Number(d.confidence ?? 0),
    outcome: (d.outcome as AgentDecision['outcome']) ?? 'pending',
    createdAt: String(d.created_at ?? ''),
  };
}

/**
 * Transform agent control response to agent state
 */
function transformControlResponse(res: Record<string, unknown>): AgentState {
  // Control responses return previous_status/current_status, not full state
  // Return a minimal AgentState with the new status
  return {
    status: (res.current_status as AgentState['status']) ?? 'stopped',
    strategyName: 'default',
    lastAction: res.message ? String(res.message) : null,
    lastActionAt: new Date().toISOString(),
    dailyPnl: 0,
    tradesToday: 0,
    errorMessage: null,
    startedAt: null,
  };
}

class AgentService {
  /**
   * Get the current agent configuration
   */
  async getConfig(): Promise<AgentConfig> {
    const res = await api.get<Record<string, unknown>>('/agent/config');
    return transformAgentConfig(res);
  }

  /**
   * Update agent configuration
   */
  async updateConfig(updates: AgentConfigUpdate): Promise<AgentConfig> {
    const res = await api.put<Record<string, unknown>>('/agent/config', updates);
    return transformAgentConfig(res);
  }

  /**
   * Get the current agent status
   */
  async getStatus(): Promise<AgentState> {
    const res = await api.get<Record<string, unknown>>('/agent/status');
    return transformAgentState(res);
  }

  /**
   * Start the trading agent
   */
  async start(): Promise<AgentState> {
    const res = await api.post<Record<string, unknown>>('/agent/start');
    return transformControlResponse(res);
  }

  /**
   * Pause the trading agent
   */
  async pause(): Promise<AgentState> {
    const res = await api.post<Record<string, unknown>>('/agent/pause');
    return transformControlResponse(res);
  }

  /**
   * Resume the trading agent
   */
  async resume(): Promise<AgentState> {
    const res = await api.post<Record<string, unknown>>('/agent/resume');
    return transformControlResponse(res);
  }

  /**
   * Stop the trading agent
   */
  async stop(): Promise<AgentState> {
    const res = await api.post<Record<string, unknown>>('/agent/stop');
    return transformControlResponse(res);
  }

  /**
   * Get paginated list of agent decisions
   */
  async getDecisions(page = 1, pageSize = 20): Promise<AgentDecisionListResponse> {
    const res = await api.get<Record<string, unknown>>(
      `/agent/decisions?page=${page}&page_size=${pageSize}`
    );
    const decisions = (res.decisions as Array<Record<string, unknown>>) || [];
    return {
      decisions: decisions.map(transformDecision),
      total: Number(res.total ?? 0),
      page: Number(res.page ?? 1),
      pageSize: Number(res.page_size ?? pageSize),
    };
  }

  /**
   * Approve an awaiting_approval decision and execute it
   */
  async approveDecision(decisionId: string): Promise<{ success: boolean; message: string }> {
    return api.post(`/agent/decisions/${decisionId}/approve`);
  }

  /**
   * Reject an awaiting_approval decision
   */
  async rejectDecision(
    decisionId: string,
    reason = 'User rejected'
  ): Promise<{ success: boolean; message: string }> {
    return api.post(`/agent/decisions/${decisionId}/reject`, { reason });
  }

  /**
   * Queue an awaiting_approval decision for market open execution
   */
  async queueDecision(decisionId: string): Promise<{ success: boolean; message: string }> {
    return api.post(`/agent/decisions/${decisionId}/queue`);
  }

  /**
   * Bulk reject all pending approval decisions
   */
  async bulkRejectAll(): Promise<{ success: boolean; message: string; affectedCount: number }> {
    const res = await api.post<Record<string, unknown>>('/agent/decisions/bulk/reject');
    return {
      success: Boolean(res.success),
      message: String(res.message ?? ''),
      affectedCount: Number(res.affected_count ?? 0),
    };
  }

  /**
   * Bulk approve all pending approval decisions
   */
  async bulkApproveAll(): Promise<{ success: boolean; message: string; affectedCount: number }> {
    const res = await api.post<Record<string, unknown>>('/agent/decisions/bulk/approve');
    return {
      success: Boolean(res.success),
      message: String(res.message ?? ''),
      affectedCount: Number(res.affected_count ?? 0),
    };
  }
}

export const agentService = new AgentService();

export default agentService;
